/**
 * Vanilla JS Modal Manager
 * Replacement for Bootstrap modal functionality
 *
 * Usage:
 *   ModalManager.open('myModalId');
 *   ModalManager.close('myModalId');
 *   ModalManager.init(); // Initialize all modals on page load
 */

var ModalManager = (function() {
  'use strict';

  var activeModal = null;
  var backdropElement = null;
  var activeTrigger = null;
  var modalIdSeed = 0;
  var initialized = false;
  var openTimer = null;
  var focusTimer = null;
  var closeTimer = null;
  var backdropTimer = null;
  var modalTokenSeed = 0;

  function clearTimers() {
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = null;
    }

    if (focusTimer) {
      clearTimeout(focusTimer);
      focusTimer = null;
    }

    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (backdropTimer) {
      clearTimeout(backdropTimer);
      backdropTimer = null;
    }
  }

  function resetModalState() {
    clearTimers();
    removeScrollBlockers();

    if (backdropElement && backdropElement.parentNode) {
      backdropElement.parentNode.removeChild(backdropElement);
    }
    backdropElement = null;

    if (activeModal) {
      activeModal.classList.remove('block', 'opacity-100');
      activeModal.classList.add('hidden');

      var dialog = getDialogElement(activeModal);
      if (dialog) {
        dialog.classList.remove('opacity-100', 'scale-100');
        dialog.classList.add('opacity-0', 'scale-95');
      }
    }

    if (document.body) {
      document.body.classList.remove('overflow-hidden');
    }

    activeModal = null;
    activeTrigger = null;
  }

  function resolveModal(modalRef) {
    if (!modalRef) return null;
    if (typeof modalRef === 'string') return document.getElementById(modalRef);
    if (modalRef.nodeType === 1) return modalRef;
    return null;
  }

  function getDialogElement(modal) {
    if (!modal) return null;
    return modal.querySelector('[data-modal-dialog], .transform');
  }

  function ensureModalId(modal) {
    if (!modal) return null;

    if (!modal.id) {
      modalIdSeed += 1;
      modal.id = 'modal-' + modalIdSeed;
    }

    return modal.id;
  }

  /**
   * Create and show backdrop overlay
   */
  function createBackdrop() {
    if (backdropElement) return;

    backdropElement = document.createElement('div');
    backdropElement.className = 'fixed inset-0 bg-gray-500 bg-opacity-50 z-40 transition-opacity duration-300';
    backdropElement.style.opacity = '0';
    document.body.appendChild(backdropElement);

    // Trigger reflow for transition
    backdropElement.offsetHeight;
    backdropElement.style.opacity = '1';

    // Close modal on backdrop click
    backdropElement.addEventListener('click', function() {
      if (activeModal) {
        close(activeModal.id);
      }
    });
  }

  function preventBackgroundScroll(event) {
    if (!activeModal) return;

    if (activeModal.contains(event.target)) {
      return;
    }

    event.preventDefault();
  }

  var wheelListener = null;
  var touchmoveListener = null;

  function addScrollBlockers() {
    if (wheelListener) return;
    wheelListener = preventBackgroundScroll;
    touchmoveListener = preventBackgroundScroll;
    document.addEventListener('wheel', wheelListener, { passive: false });
    document.addEventListener('touchmove', touchmoveListener, { passive: false });
  }

  function removeScrollBlockers() {
    if (wheelListener) {
      document.removeEventListener('wheel', wheelListener, { passive: false });
      wheelListener = null;
    }
    if (touchmoveListener) {
      document.removeEventListener('touchmove', touchmoveListener, { passive: false });
      touchmoveListener = null;
    }
  }

  /**
   * Remove backdrop overlay
   */
  function removeBackdrop() {
    if (!backdropElement) return;

    backdropElement.style.opacity = '0';
    if (backdropTimer) {
      clearTimeout(backdropTimer);
    }

    backdropTimer = setTimeout(function() {
      if (backdropElement && backdropElement.parentNode) {
        backdropElement.parentNode.removeChild(backdropElement);
        backdropElement = null;
      }
      backdropTimer = null;
    }, 300);
  }

  /**
   * Open a modal by ID
   */
  function open(modalRef, triggerEl) {
    var modal = resolveModal(modalRef);
    if (!modal) {
      console.error('Modal not found:', modalRef);
      return;
    }

    if (activeModal === modal && !modal.classList.contains('hidden')) {
      return;
    }

    // Close any existing modal first
    if (activeModal && activeModal !== modal) {
      resetModalState();
    } else {
      clearTimers();
    }

    activeModal = modal;
    activeTrigger = triggerEl || null;
    modalTokenSeed += 1;
    modal.setAttribute('data-modal-token', String(modalTokenSeed));
    createBackdrop();
    addScrollBlockers();

    // Show modal
    modal.classList.remove('hidden');
    modal.classList.add('block');
    document.body.classList.add('overflow-hidden');

    // Trigger animation
    openTimer = setTimeout(function() {
      modal.classList.add('opacity-100');
      var dialog = getDialogElement(modal);
      if (dialog) {
        dialog.classList.remove('opacity-0', 'scale-95');
        dialog.classList.add('opacity-100');
        dialog.classList.add('scale-100');
      }
      openTimer = null;
    }, 10);

    // Focus first input in modal
    var firstInput = modal.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])') || modal.querySelector('button');
    if (firstInput) {
      focusTimer = setTimeout(function() {
        firstInput.focus();
        focusTimer = null;
      }, 1000);
    }

    // Emit custom event
    var event = new window.CustomEvent('modal:opened', { detail: { modalId: ensureModalId(modal), trigger: activeTrigger, token: String(modalTokenSeed) } });
    document.dispatchEvent(event);
  }

  /**
   * Close a modal by ID
   */
  function close(modalRef) {
    var modal = resolveModal(modalRef || activeModal);
    if (!modal) return;

    if (modal.classList.contains('hidden') && activeModal !== modal) {
      return;
    }

    // Trigger closing animation
    clearTimers();
    var closeToken = modal.getAttribute('data-modal-token');
    modal.classList.remove('opacity-100');
    var dialog = getDialogElement(modal);
    if (dialog) {
      dialog.classList.remove('opacity-100', 'scale-100');
      dialog.classList.add('opacity-0', 'scale-95');
      dialog.classList.remove('scale-100');
    }

    closeTimer = setTimeout(function() {
      modal.classList.remove('block');
      modal.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
      removeScrollBlockers();
      removeBackdrop();
      if (activeModal === modal) activeModal = null;
      activeTrigger = null;

      // Emit custom event
      var event = new window.CustomEvent('modal:closed', { detail: { modalId: ensureModalId(modal), token: closeToken } });
      document.dispatchEvent(event);
      closeTimer = null;
    }, 300);
  }

  function toggle(modalRef, triggerEl) {
    var modal = resolveModal(modalRef);
    if (!modal) return;

    if (modal.classList.contains('hidden')) {
      open(modal, triggerEl);
    } else {
      close(modal);
    }
  }

  /**
   * Handle ESC key to close modal
   */
  function handleEscape(e) {
    if (e.key === 'Escape' && activeModal) {
      close(activeModal.id);
    }
  }

  /**
   * Initialize all modal triggers
   */
  function init() {
    if (initialized) return;
    initialized = true;

    // Add ESC key listener
    document.addEventListener('keydown', handleEscape);

    document.addEventListener('click', function(e) {
      var openTrigger = e.target.closest('[data-modal-open]');
      if (openTrigger) {
        e.preventDefault();
        open(openTrigger.getAttribute('data-modal-open'), openTrigger);
        return;
      }

      var closeTrigger = e.target.closest('[data-modal-close]');
      if (closeTrigger) {
        e.preventDefault();
        var modalId = closeTrigger.getAttribute('data-modal-close') || (activeModal ? activeModal.id : null);
        if (modalId) {
          close(modalId);
        }
      }
    });

    document.addEventListener('turbo:before-cache', resetModalState);
    document.addEventListener('turbolinks:before-cache', resetModalState);
    window.addEventListener('pagehide', resetModalState);
  }

  if (window.jQuery && !window.jQuery.fn.modal) {
    window.jQuery.fn.modal = function(action) {
      return this.each(function() {
        if (action === 'hide') {
          close(this);
        } else if (action === 'toggle') {
          toggle(this);
        } else {
          open(this);
        }
      });
    };
  }

  // Public API
  return {
    init: init,
    open: open,
    close: close,
    toggle: toggle
  };
})();

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ModalManager.init);
} else {
  ModalManager.init();
}

document.addEventListener('turbo:load', ModalManager.init);
document.addEventListener('turbolinks:load', ModalManager.init);

window.ModalManager = ModalManager;
